import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import Tether from 'tether'
import {
  Popover,
  PopoverList,
  PopoverListItem,
} from '@lightspeed/design-system-react'
import { reactular } from '@vendhq/reactular'
import { createPortal } from 'react-dom'
import { T } from '@transifex/react'
import { trackUserEvent } from '@vend/utilities'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  closeRegisterActionsPopover,
  selectShowRegisterActionsPopover,
  setShowTrainingModeModal,
} from '../../../state/slices/uiStateSlice'
import { useIsOffline } from '../../shared/useIsOffline'
import {
  useSaleManager,
  useSaleSyncManager,
  useTrainingModeManager,
} from '../../../ng-compat/hooks'
import { WEB_REGISTER } from '../../web-register.constant'
import { STATES } from '../../../../../lib/routes'
import { $state } from '../../../ng-compat/imports'
import { ExitTrainingModeModal } from './ExitTrainingModeModal'
import { SelectRegister } from './SelectRegister'
import { ActionOfflineSales } from './ActionOfflineSales'
import { ActionErroredSales } from './ActionErroredSales'

const CLOSE_MODAL_EVENT = 'vend-ui-overlay-opened'

const trackRegisterAction = (event: string) => {
  trackUserEvent(WEB_REGISTER.APPLICATION_NAME, 'Sidenav Register Actions', {
    Event: event,
  })
}

export const RegisterActions: FunctionComponent = () => {
  const show = useAppSelector(selectShowRegisterActionsPopover)
  const dispatch = useAppDispatch()
  const trainingModeManager = useTrainingModeManager()
  const saleSyncManager = useSaleSyncManager()
  const saleManager = useSaleManager()
  const { isOffline } = useIsOffline()
  const [element, setElement] = useState<HTMLElement | null>(null)
  const tetherRef = useRef<Tether | null>(null)
  const [showExitModal, setShowExitModal] = useState(false)
  const [showSelectRegisterModal, setShowSelectRegisterModal] = useState(false)
  const [
    showActionOfflineSalesModal,
    setShowActionOfflineSalesModal,
  ] = useState(false)
  const [
    showActionErroredSalesModal,
    setShowActionErroredSalesModal,
  ] = useState(false)

  const trainingModeEnabled = useAppSelector(
    state => state.posState.trainingModeEnabled
  )
  const showSwitchRegister = !isOffline && !trainingModeEnabled
  const showSyncSales = Boolean(saleSyncManager.uploadSalesCount)

  useEffect(() => {
    if (show && element) {
      const els = document.getElementsByClassName(
        'register-info-popover-trigger'
      )

      if (!els.length) {
        return
      }

      const target = els[0] as HTMLElement

      // The target is part of the Nav so we need to use Tether directly
      tetherRef.current = new Tether({
        element,
        target,
        attachment: 'top left',
        targetAttachment: 'bottom left',
      })
      tetherRef.current.enable()
    }
  }, [element, show])

  const close = () => {
    document.dispatchEvent(new CustomEvent(CLOSE_MODAL_EVENT))
    dispatch(closeRegisterActionsPopover())
  }

  const toggleTrainingMode = async () => {
    close()
    if (trainingModeEnabled) {
      trackRegisterAction('Exit Training Mode')
      setShowExitModal(true)
    } else {
      trackRegisterAction('Switch to Training Mode')
      const response = await saleManager.actionExistingSaleIfRequired({
        context: 'TRAINING_MODE',
      })
      if (response.hasActiveSale) {
        if (!$state.includes(STATES.CURRENT_SALE)) {
          $state.go(STATES.SALE)
        }
        return Promise.reject(new Error('Active sale in progress'))
      }
      if ($state.includes(STATES.PAYMENT_PANEL)) {
        $state.go(STATES.SALE)
      }
      trainingModeManager.initiateTrainingMode()
      dispatch(setShowTrainingModeModal(true))
      trainingModeManager.initiateTrainingTour()
    }
  }

  const canSwitchRegister = async () => {
    if (saleSyncManager.uploadSalesCount) {
      setShowActionOfflineSalesModal(true)
      return false
    }
    if (
      saleSyncManager.erroredSalesCount ||
      saleSyncManager.attemptedSalesCount
    ) {
      setShowActionErroredSalesModal(true)
      return false
    }
    const response = await saleManager.actionExistingSaleIfRequired({
      context: 'SWITCH_REGISTER',
    })
    if (response.hasActiveSale) {
      return false
    }
    return true
  }

  const switchRegister = async () => {
    close()
    const canSwitch = await canSwitchRegister()
    if (canSwitch) {
      trackRegisterAction('Switch Register')
      setShowSelectRegisterModal(true)
    }
  }

  const exitTrainingMode = async () => {
    setShowExitModal(false)
    await trainingModeManager.exitTrainingMode()
    $state.go(STATES.SALE, {}, { location: 'replace' })
  }

  const handleActionOfflineSalesClose = (salesSynced?: boolean) => {
    setShowActionOfflineSalesModal(false)
    if (salesSynced) {
      switchRegister()
    }
  }

  const syncOfflineSales = () => {
    close()
    trackRegisterAction('Sync Offline Sales')
    saleSyncManager.retryUnsyncedSales()
  }

  return (
    <>
      {show
        ? createPortal(
            <Popover
              onClose={close}
              ref={setElement}
              className="vd-popover-container--inside-modal vd-popover--with-list vd-pa0"
            >
              <PopoverList>
                {showSwitchRegister ? (
                  <PopoverListItem
                    index={1}
                    key="switch-register"
                    onClick={switchRegister}
                  >
                    <button className="register-actions-item">
                      <T _str="Switch Register" />
                    </button>
                  </PopoverListItem>
                ) : null}
                <PopoverListItem
                  index={2}
                  key="training-mode"
                  onClick={toggleTrainingMode}
                >
                  <button className="register-actions-item">
                    {trainingModeEnabled ? (
                      <T _str="Exit Training Mode" />
                    ) : (
                      <T _str="Switch to Training Mode" />
                    )}
                  </button>
                </PopoverListItem>
                {showSyncSales ? (
                  <PopoverListItem
                    index={3}
                    key="sale-sync"
                    onClick={syncOfflineSales}
                  >
                    <button className="register-actions-item">
                      <T _str="Sync Offline Sales" />
                    </button>
                  </PopoverListItem>
                ) : null}
              </PopoverList>
            </Popover>,
            document.body
          )
        : null}
      {showExitModal
        ? createPortal(
            <ExitTrainingModeModal
              cancel={() => {
                setShowExitModal(false)
              }}
              confirm={exitTrainingMode}
            />,
            document.body
          )
        : null}
      {showSelectRegisterModal ? (
        <SelectRegister
          onClose={() => setShowSelectRegisterModal(false)}
          dismissable={true}
        />
      ) : null}
      {showActionOfflineSalesModal ? (
        <ActionOfflineSales close={handleActionOfflineSalesClose} />
      ) : null}
      {showActionErroredSalesModal ? (
        <ActionErroredSales
          close={() => setShowActionErroredSalesModal(false)}
        />
      ) : null}
    </>
  )
}

export const ReactRegisterActionsComponent = reactular(
  RegisterActions,
  [],
  'reactWrapper'
)
